import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import type { Color3 } from "@babylonjs/core/Maths/math.color";
import { CreateCylinder } from "@babylonjs/core/Meshes/Builders/cylinderBuilder";
import { CreateTorus } from "@babylonjs/core/Meshes/Builders/torusBuilder";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Scene } from "@babylonjs/core/scene";
import { CASINO_RULES, MONEY_WHEEL_SEGMENTS, type CasinoDrink, type CasinoState, type MoneyWheelSymbol } from "@townsy/contracts";
import { CASINO_SLOTS, CASINO_TABLES, CASINO_WHEEL } from "@townsy/world";
import { PartBatch, applyPart, cylinder, sphere } from "../../../scene/core/meshKit";
import { CHIP_COLORS, chipStack, reelSpinPosition, wheelRestAngle, wheelSpinAngle, wrapStop } from "../casinoView";
import { InstancedParts, hex, litVertex, paintedTexture, unlitTexture, unlitVertex, type Bag } from "./kit";
import { CARD_ATLAS, REEL_ATLAS, SLOT_COLORS, WHEEL_TEX, cardUv, paintCards, paintReels, paintWheel, reelWindowUv } from "./paint";
import { QuadBatch } from "./parts";
import { CARD_RING, CHIP_RING, COUNTER_TOP_Y, FELT_Y, REEL_Y } from "./staticBuild";

/**
 * The casino's moving parts (ADR-0016), driven only by the server's shared state: cards and chips on the blackjack
 * tables, the slot reels spinning to the server's stops, the money wheel turning to the server's segment, chips on the
 * wheel's layout, and the free mocktails in visitors' hands.
 */
const CHIP_H = 0.018;
const CARD_W = 0.16;
const CARD_H = 0.23;

function chipMesh(scene: Scene, name: string): Mesh {
  const m = CreateCylinder(name, { height: CHIP_H, diameter: 0.1, tessellation: 18 }, scene);
  applyPart(m, "#FFFFFF");
  return m;
}

function pushStack(parts: InstancedParts, x: number, y: number, z: number, amount: number): void {
  chipStack(amount, 12).forEach((v, k) => parts.push(x, y + CHIP_H / 2 + k * CHIP_H, z, hex(CHIP_COLORS[v]), 1, k * 0.7));
}

// ---------------------------------------------------------------------------------------------------------------------
// Blackjack tables
// ---------------------------------------------------------------------------------------------------------------------

export class TablesView {
  private readonly cards: Mesh[] = [];
  private readonly batches: QuadBatch[] = [];
  private readonly chips: InstancedParts;
  private readonly rings: InstancedParts;
  private state: CasinoState | null = null;

  constructor(
    private readonly scene: Scene,
    bag: Bag,
  ) {
    const tex = paintedTexture(bag, scene, "casinoCardsTex", CARD_ATLAS.width, CARD_ATLAS.height, paintCards);
    const mat = bag.add(unlitTexture(scene, "casinoCards", tex));
    for (const t of CASINO_TABLES) {
      const batch = new QuadBatch();
      const mesh = bag.add(batch.build(`casinoCards${t.index}`, scene, true));
      mesh.material = mat;
      this.cards.push(mesh);
      this.batches.push(batch);
    }
    const lit = bag.add(litVertex(scene, "casinoChipMat"));
    const chip = bag.add(chipMesh(scene, "casinoTableChips"));
    chip.material = lit;
    this.chips = new InstancedParts(chip, 400);
    const ring = bag.add(CreateTorus("casinoRings", { diameter: 0.36, thickness: 0.035, tessellation: 24 }, scene));
    applyPart(ring, "#FFFFFF");
    ring.material = bag.add(unlitVertex(scene, "casinoRingMat"));
    this.rings = new InstancedParts(ring, 32);
  }

  update(s: CasinoState): void {
    this.state = s;
    this.chips.begin();
    s.tables.forEach((t, ti) => {
      const L = CASINO_TABLES[ti];
      const batch = this.batches[ti];
      const mesh = this.cards[ti];
      if (!L || !batch || !mesh) return;
      batch.clear();
      const { x: px, z: pz } = L.pivot;
      // Dealer's cards in a row in front of the chip rack, readable from the players' side.
      t.dealer.forEach((c, k) => {
        const x = px + (k - (t.dealer.length - 1) / 2) * 0.19;
        batch.flat(x, FELT_Y + 0.004 + k * 0.0005, pz - 0.52, CARD_W, CARD_H, 0, 1, cardUv(c));
      });
      t.seats.forEach((seat, si) => {
        const d = L.seatDirs[si];
        if (!seat || !d) return;
        const right = { x: -d.z, z: d.x };
        const n = seat.cards.length;
        const step = n > 1 ? Math.min(0.17, 0.32 / (n - 1)) : 0;
        seat.cards.forEach((c, k) => {
          const off = (k - (n - 1) / 2) * step;
          const r = L.radius * CARD_RING - k * 0.012;
          batch.flat(px + d.x * r + right.x * off, FELT_Y + 0.004 + k * 0.0005, pz + d.z * r + right.z * off, CARD_W, CARD_H, -d.x, -d.z, cardUv(c));
        });
        // The stake in the betting circle; winnings beside it once the round is settled.
        const cr = L.radius * CHIP_RING;
        const cx = px + d.x * cr;
        const cz = pz + d.z * cr;
        if (seat.bet > 0) pushStack(this.chips, cx, FELT_Y, cz, seat.bet);
        if (seat.outcome && seat.payout > seat.bet) pushStack(this.chips, cx + right.x * 0.12, FELT_Y, cz + right.z * 0.12, seat.payout - seat.bet);
      });
      batch.applyTo(mesh, true);
      mesh.setEnabled(batch.quads > 0);
    });
    this.chips.commit();
  }

  frame(now: number): void {
    const s = this.state;
    this.rings.begin();
    if (s) {
      s.tables.forEach((t, ti) => {
        const L = CASINO_TABLES[ti];
        if (!L) return;
        t.seats.forEach((seat, si) => {
          const d = L.seatDirs[si];
          if (!seat || !d) return;
          const cr = L.radius * CHIP_RING;
          const x = L.pivot.x + d.x * cr;
          const z = L.pivot.z + d.z * cr;
          let color: Color3 | null = null;
          let scale = 1;
          if (t.phase === "playing" && t.turn === si) {
            color = hex("#FFD24A");
            scale = 1 + 0.12 * Math.sin(now / 160);
          } else if (seat.outcome === "win" || seat.outcome === "blackjack") color = hex("#3DDC84");
          else if (seat.outcome === "push") color = hex("#A9C1F0");
          else if (seat.outcome === "lose" || seat.outcome === "bust") color = hex("#FF5A5A");
          if (color) this.rings.push(x, FELT_Y + 0.006, z, color, scale);
        });
      });
    }
    this.rings.commit();
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Slot machines
// ---------------------------------------------------------------------------------------------------------------------

interface ReelAnim {
  from: number[];
  to: number[];
  start: number;
  ends: number[];
}

export class SlotsView {
  private readonly mesh: Mesh;
  private readonly batch = new QuadBatch();
  /** Displayed reel positions (fractional stops), machine-major. */
  private readonly pos: number[] = new Array<number>(CASINO_SLOTS.length * 3).fill(0);
  private readonly anims: Array<ReelAnim | null> = CASINO_SLOTS.map(() => null);
  private readonly spins: number[] = CASINO_SLOTS.map(() => -1);
  private readonly winAt: number[] = CASINO_SLOTS.map(() => -Infinity);
  private readonly winSpin: number[] = CASINO_SLOTS.map(() => -1);
  private readonly busy: boolean[] = CASINO_SLOTS.map(() => false);
  private readonly lamps: InstancedParts;
  private dirty = true;

  constructor(scene: Scene, bag: Bag) {
    const tex = paintedTexture(bag, scene, "casinoReelsTex", REEL_ATLAS.width, REEL_ATLAS.height, paintReels, { wrap: true });
    const mat = bag.add(unlitTexture(scene, "casinoReels", tex));
    this.writeQuads();
    this.mesh = bag.add(this.batch.build("casinoReelsMesh", scene, true));
    this.mesh.material = mat;
    const lamp = bag.add(sphere(scene, "casinoSlotLamps", 0.13, 10, "#FFFFFF"));
    lamp.material = bag.add(unlitVertex(scene, "casinoLampMat"));
    this.lamps = new InstancedParts(lamp, CASINO_SLOTS.length);
  }

  private writeQuads(): void {
    this.batch.clear();
    CASINO_SLOTS.forEach((s, i) => {
      for (let r = 0; r < 3; r++) {
        this.batch.upright(s.position.x + (r - 1) * 0.25, REEL_Y, s.position.z - 0.263, 0.22, 0.5, Math.PI, reelWindowUv(r, this.pos[i * 3 + r]!));
      }
    });
  }

  update(s: CasinoState, at: number): void {
    s.slots.forEach((m, i) => {
      if (i >= CASINO_SLOTS.length) return;
      this.busy[i] = m.actorId !== null;
      if (m.spin !== this.spins[i]) {
        const first = this.spins[i] === -1;
        this.spins[i] = m.spin;
        if (m.spinning && !first) {
          const total = Math.max(300, m.endsIn ?? CASINO_RULES.slots.spinMs);
          this.anims[i] = {
            from: [0, 1, 2].map((r) => this.pos[i * 3 + r]!),
            to: [...m.stops],
            start: at,
            ends: [0, 1, 2].map((r) => at + total * (0.55 + 0.225 * r)),
          };
        } else {
          // Joined after (or in the middle of) a spin: show where the reels stop.
          this.anims[i] = null;
          for (let r = 0; r < 3; r++) this.pos[i * 3 + r] = m.stops[r] ?? 0;
          this.dirty = true;
          if (!m.spinning && m.win > 0) this.winSpin[i] = m.spin;
        }
      }
      if (!m.spinning && m.win > 0 && this.winSpin[i] !== m.spin) {
        this.winSpin[i] = m.spin;
        this.winAt[i] = at;
      }
    });
  }

  frame(now: number): void {
    this.anims.forEach((a, i) => {
      if (!a) return;
      let done = true;
      for (let r = 0; r < 3; r++) {
        const end = a.ends[r]!;
        const p = Math.min(1, (now - a.start) / Math.max(1, end - a.start));
        if (p < 1) done = false;
        this.pos[i * 3 + r] = wrapStop(reelSpinPosition(a.from[r]!, a.to[r]!, 2 + r, p));
      }
      this.dirty = true;
      if (done) {
        for (let r = 0; r < 3; r++) this.pos[i * 3 + r] = a.to[r]!;
        this.anims[i] = null;
      }
    });
    if (this.dirty) {
      this.dirty = false;
      this.writeQuads();
      this.mesh.updateVerticesData(VertexBuffer.UVKind, this.batch.uvs);
    }
    // Candle lamps: the machine's colour, brighter while someone plays, flashing after a win.
    this.lamps.begin();
    CASINO_SLOTS.forEach((s, i) => {
      const base = hex(SLOT_COLORS[i % SLOT_COLORS.length]!);
      const since = now - this.winAt[i]!;
      let c = base.scale(this.busy[i] ? 1 : 0.55);
      if (since < 4000) c = Math.floor(since / 180) % 2 ? hex("#FFFFFF") : hex("#FFD24A");
      this.lamps.push(s.position.x, 2.56, s.position.z + 0.25, c);
    });
    this.lamps.commit();
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Money wheel
// ---------------------------------------------------------------------------------------------------------------------

/** rotation.z is counter-clockwise as seen from the front; the wheel maths count clockwise. */
const SPIN_SIGN = -1;
const BULBS = 28;

export class WheelView {
  private readonly rotor: TransformNode;
  private readonly rotorMeshes: Mesh[] = [];
  private angle = 0;
  private anim: { from: number; segment: number; start: number; duration: number } | null = null;
  private phase: string | null = null;
  private round = -1;
  private resultSymbol: MoneyWheelSymbol | null = null;
  private resultAt = -Infinity;
  private readonly bulbs: InstancedParts;
  private readonly chips: InstancedParts;
  private readonly highlights = new Map<string, Mesh>();

  constructor(
    scene: Scene,
    bag: Bag,
    private readonly cells: Array<{ symbol: string; x0: number; x1: number; z: number }>,
  ) {
    const W = CASINO_WHEEL;
    this.rotor = bag.add(new TransformNode("casinoWheelRotor", scene));
    this.rotor.position.set(W.position.x, W.hubY, W.position.z - 0.02);
    // The face: a disc of the painted wheel (u right, v up as seen from the front).
    const tex = paintedTexture(bag, scene, "casinoWheelTex", WHEEL_TEX, WHEEL_TEX, paintWheel);
    const face = new QuadBatch();
    const n = 108;
    const R = W.radius;
    face.positions.push(0, 0, -0.07);
    face.normals.push(0, 0, -1);
    face.uvs.push(0.5, 0.5);
    for (let i = 0; i <= n; i++) {
      const a = (Math.PI * 2 * i) / n;
      face.positions.push(Math.cos(a) * R, Math.sin(a) * R, -0.07);
      face.normals.push(0, 0, -1);
      face.uvs.push(0.5 + Math.cos(a) / 2, 0.5 + Math.sin(a) / 2);
    }
    for (let i = 0; i < n; i++) face.indices.push(0, i + 1, i + 2);
    const faceMesh = bag.add(face.build("casinoWheelFace", scene));
    faceMesh.material = bag.add(unlitTexture(scene, "casinoWheelFaceMat", tex));
    faceMesh.parent = this.rotor;
    this.rotorMeshes.push(faceMesh);
    // Rim, pegs between segments and a hub cap turn with the face.
    const b = new PartBatch();
    const rim = CreateCylinder("wheelRim", { height: 0.12, diameter: R * 2 + 0.06, tessellation: 64 }, scene);
    applyPart(rim, "#3A2412", { rot: { x: Math.PI / 2 } });
    b.add(rim);
    for (let k = 0; k < MONEY_WHEEL_SEGMENTS.length; k++) {
      const a = Math.PI / 2 - (k + 0.5) * ((Math.PI * 2) / MONEY_WHEEL_SEGMENTS.length);
      b.add(cylinder(scene, "wheelPeg", { height: 0.08, diameter: 0.025, tessellation: 6 }, "#F2D06B", { pos: { x: Math.cos(a) * (R - 0.03), y: Math.sin(a) * (R - 0.03), z: -0.1 }, rot: { x: Math.PI / 2 } }));
    }
    b.add(sphere(scene, "wheelHub", 0.22, 10, "#E8C45A", { pos: { x: 0, y: 0, z: -0.08 } }));
    const rimMesh = b.build("casinoWheelRim", scene);
    if (rimMesh) {
      bag.add(rimMesh);
      rimMesh.material = bag.add(litVertex(scene, "casinoWheelRimMat"));
      rimMesh.parent = this.rotor;
      this.rotorMeshes.push(rimMesh);
    }
    // Bulbs on the fixed frame (chase while the wheel spins).
    const bulb = bag.add(sphere(scene, "casinoWheelBulbs", 0.08, 8, "#FFFFFF"));
    bulb.material = bag.add(unlitVertex(scene, "casinoBulbMat"));
    this.bulbs = new InstancedParts(bulb, BULBS);
    // Chips on the betting layout.
    const chip = bag.add(chipMesh(scene, "casinoLayoutChips"));
    chip.material = bag.add(litVertex(scene, "casinoLayoutChipMat"));
    this.chips = new InstancedParts(chip, 120);
    // A glowing frame around each layout cell (shown for the winner).
    const glow = bag.add(unlitVertex(scene, "casinoCellGlow"));
    for (const c of cells) {
      const fb = new PartBatch();
      const w = c.x1 - c.x0;
      const cx = (c.x0 + c.x1) / 2;
      const y = COUNTER_TOP_Y + 0.01;
      fb.box(w + 0.1, 0.03, 0.05, "#FFF3B0", cx, y, c.z - 0.29);
      fb.box(w + 0.1, 0.03, 0.05, "#FFF3B0", cx, y, c.z + 0.29);
      fb.box(0.05, 0.03, 0.63, "#FFF3B0", c.x0 - 0.03, y, c.z);
      fb.box(0.05, 0.03, 0.63, "#FFF3B0", c.x1 + 0.03, y, c.z);
      const m = fb.build(`casinoCell_${c.symbol}`, scene);
      if (!m) continue;
      bag.add(m);
      m.material = glow;
      m.setEnabled(false);
      this.highlights.set(c.symbol, m);
    }
  }

  casters(): Mesh[] {
    return this.rotorMeshes;
  }

  update(s: CasinoState, at: number): void {
    const w = s.wheel;
    const spinStart = w.phase === "spinning" && (this.phase !== "spinning" || this.round !== w.round);
    if (spinStart && w.segment !== null) {
      const elapsed = Math.max(0, CASINO_RULES.wheel.spinMs - w.endsIn);
      // Joining mid-spin: start the animation from where it would be.
      this.anim = { from: this.angle, segment: w.segment, start: at - elapsed, duration: CASINO_RULES.wheel.spinMs };
    } else if (w.phase !== "spinning" && w.segment !== null && !this.anim) {
      this.angle = wheelRestAngle(w.segment);
    }
    if (w.phase === "result" && w.result && (this.phase !== "result" || this.round !== w.round)) {
      this.resultSymbol = w.result;
      this.resultAt = at;
    }
    if (w.phase !== "result") this.resultSymbol = null;
    this.phase = w.phase;
    this.round = w.round;
    // Chips on the layout: everyone's bets per symbol this round.
    this.chips.begin();
    for (const c of this.cells) {
      const amount = w.pot[c.symbol as MoneyWheelSymbol] ?? 0;
      if (amount > 0) pushStack(this.chips, c.x1 - 0.13, COUNTER_TOP_Y, c.z + 0.17, amount);
    }
    this.chips.commit();
    for (const [symbol, m] of this.highlights) m.setEnabled(this.resultSymbol === symbol);
  }

  frame(now: number): void {
    const a = this.anim;
    if (a) {
      const p = (now - a.start) / a.duration;
      if (p >= 1) {
        this.angle = wheelRestAngle(a.segment);
        this.anim = null;
      } else {
        this.angle = wheelSpinAngle(a.from, a.segment, 5, p);
      }
    }
    this.rotor.rotation.z = SPIN_SIGN * this.angle;
    // Bulbs: a chase while spinning, flashing on a result, a calm glow otherwise.
    const W = CASINO_WHEEL;
    const R = W.radius + 0.1;
    const spinning = this.anim !== null;
    const flashing = this.resultSymbol !== null && now - this.resultAt < 5000;
    const head = Math.floor(now / 70) % BULBS;
    this.bulbs.begin();
    for (let k = 0; k < BULBS; k++) {
      const ang = (Math.PI * 2 * k) / BULBS;
      let c: Color3;
      if (spinning) c = (k - head + BULBS) % 7 === 0 ? hex("#FFFFFF") : hex("#C98A2B");
      else if (flashing) c = Math.floor(now / 250) % 2 === k % 2 ? hex("#FFF3B0") : hex("#E3262E");
      else c = hex("#FFD27A");
      this.bulbs.push(Math.cos(ang) * R, W.hubY + Math.sin(ang) * R, W.position.z - 0.08, c);
    }
    this.bulbs.commit();
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Free mocktails in visitors' hands
// ---------------------------------------------------------------------------------------------------------------------

const DRINK_COLORS: Record<CasinoDrink, string> = {
  sunset_fizz: "#F28A3C",
  mint_cooler: "#5CC79A",
  berry_spritz: "#C24A86",
  lemon_sparkle: "#F2D04A",
};

export class DrinksView {
  private readonly shown = new Map<string, { mesh: Mesh; drink: CasinoDrink }>();
  private readonly mat;

  constructor(
    private readonly scene: Scene,
    bag: Bag,
  ) {
    this.mat = bag.add(litVertex(scene, "casinoDrinkMat"));
  }

  update(s: CasinoState): void {
    const want = new Map(s.drinks.map((d) => [d.actorId, d.drink] as const));
    for (const [actorId, d] of this.shown) {
      if (want.get(actorId) !== d.drink) {
        d.mesh.dispose();
        this.shown.delete(actorId);
      }
    }
    for (const [actorId, drink] of want) {
      if (this.shown.has(actorId)) continue;
      const b = new PartBatch();
      b.add(cylinder(this.scene, "glass", { height: 0.16, diameterTop: 0.1, diameterBottom: 0.075, tessellation: 10 }, "#D8EEF6", { pos: { x: 0, y: 0.08, z: 0 } }));
      b.add(cylinder(this.scene, "drink", { height: 0.12, diameterTop: 0.094, diameterBottom: 0.072, tessellation: 10 }, DRINK_COLORS[drink], { pos: { x: 0, y: 0.075, z: 0 } }));
      b.box(0.012, 0.2, 0.012, "#FFFFFF", 0.02, 0.17, 0, 0);
      b.add(sphere(this.scene, "fruit", 0.05, 6, "#F2D04A", { pos: { x: -0.045, y: 0.16, z: 0 } }));
      const mesh = b.build(`casinoDrink_${actorId}`, this.scene);
      if (!mesh) continue;
      mesh.material = this.mat;
      mesh.isPickable = false;
      mesh.setEnabled(false);
      this.shown.set(actorId, { mesh, drink });
    }
  }

  /** Each drink follows its visitor's avatar (right hand); hidden while the avatar is not drawn. */
  frame(): void {
    for (const [actorId, d] of this.shown) {
      const node = this.scene.getTransformNodeByName(`av_${actorId}`);
      if (!node || node.isDisposed() || !node.isEnabled()) {
        d.mesh.setEnabled(false);
        continue;
      }
      const f = node.rotation.y;
      const ox = 0.3;
      const oz = 0.14;
      d.mesh.position.set(node.position.x + ox * Math.cos(f) + oz * Math.sin(f), node.position.y + 0.92, node.position.z - ox * Math.sin(f) + oz * Math.cos(f));
      d.mesh.rotation.y = f;
      d.mesh.setEnabled(true);
    }
  }

  dispose(): void {
    for (const d of this.shown.values()) d.mesh.dispose();
    this.shown.clear();
  }
}
